import { useEffect, useState, useCallback, useMemo } from 'react';

// Reusable guided-tutorial hook. Pass an array of steps (each with a key,
// title, body, and a ref returned from useRef pointing at the DOM node to
// highlight). The hook returns:
//   - open: boolean - whether the tutorial overlay is currently shown
//   - start() / close() - imperative controls
//   - currentStep, totalSteps - position
//   - next() / back() - navigation
//   - isStepActive(key) - returns true when the given step's key is active;
//     use to apply a highlight class to your target DOM nodes
//   - cardStyle - inline style for positioning the overlay card next to
//     the active target (handles right / left / below fallbacks)
//   - currentStepDef - the active step object (or null)
//
// Extracted from the original FacilitatorCaseLibrary tutorial so the four
// new module editors can share the same UX.
export default function useGuidedTutorial(steps = []) {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [cardStyle, setCardStyle] = useState({});

  const totalSteps = steps.length;
  const currentStepDef = open ? steps[currentStep] : null;

  const start = useCallback(() => {
    setCurrentStep(0);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setCurrentStep(0);
  }, []);

  const next = useCallback(() => {
    setCurrentStep((curr) => {
      if (curr >= totalSteps - 1) {
        setOpen(false);
        return 0;
      }
      return curr + 1;
    });
  }, [totalSteps]);

  const back = useCallback(() => {
    setCurrentStep((curr) => Math.max(0, curr - 1));
  }, []);

  const isStepActive = useCallback(
    (key) => open && steps[currentStep]?.key === key,
    [open, currentStep, steps]
  );

  // Scroll the active target into view when step changes.
  useEffect(() => {
    if (!open) return;
    const targetNode = steps[currentStep]?.ref?.current;
    if (targetNode?.scrollIntoView) {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [open, currentStep, steps]);

  // Position the overlay card next to the active target. Handles resize +
  // scroll. Falls back to centred when no target is available.
  useEffect(() => {
    if (!open) {
      setCardStyle({});
      return undefined;
    }

    const updateCardPosition = () => {
      const targetNode = steps[currentStep]?.ref?.current;
      if (!targetNode) {
        setCardStyle({
          width: 'min(420px, calc(100vw - 2rem))',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        });
        return;
      }
      const rect = targetNode.getBoundingClientRect();
      const cardWidth = Math.min(420, window.innerWidth - 32);
      const gutter = 16;
      const canPlaceRight = rect.right + gutter + cardWidth <= window.innerWidth - 16;
      const canPlaceLeft = rect.left - gutter - cardWidth >= 16;
      let left;
      let top;
      if (canPlaceRight) {
        left = rect.right + gutter;
        top = rect.top;
      } else if (canPlaceLeft) {
        left = rect.left - gutter - cardWidth;
        top = rect.top;
      } else {
        left = Math.max(16, Math.min(rect.left, window.innerWidth - cardWidth - 16));
        top = rect.bottom + gutter;
        if (top + 240 > window.innerHeight) {
          top = Math.max(16, rect.top - 240 - gutter);
        }
      }
      setCardStyle({
        width: `${cardWidth}px`,
        top: `${Math.max(16, top)}px`,
        left: `${Math.max(16, left)}px`,
        transform: 'none',
      });
    };

    updateCardPosition();
    window.addEventListener('resize', updateCardPosition);
    window.addEventListener('scroll', updateCardPosition, true);
    return () => {
      window.removeEventListener('resize', updateCardPosition);
      window.removeEventListener('scroll', updateCardPosition, true);
    };
  }, [open, currentStep, steps]);

  return useMemo(() => ({
    open,
    start,
    close,
    next,
    back,
    currentStep,
    totalSteps,
    isStepActive,
    cardStyle,
    currentStepDef,
  }), [open, start, close, next, back, currentStep, totalSteps, isStepActive, cardStyle, currentStepDef]);
}
